import { Text } from '@cad-arc';
import { useDurationSince } from '../hooks/use-duration-since';

const MdText = ({ children }: { children: React.ReactNode }) => (
    <Text variant="bodyMd" as="span" color="inherit">
        {children}
    </Text>
);

const CaptionText = ({ children }: { children: React.ReactNode }) => (
    <Text variant="caption" as="span" color="inherit">
        {children}
    </Text>
);

export function FormattedDurationSinceDetailed({
    date,
    signal,
}: {
    date: Date;
    signal?: unknown;
}): React.ReactElement {
    const duration = useDurationSince({ date, signal, interval: 10000 });

    // display all time units up to the highest populated unit
    const months = duration.months || duration.years ? duration.months : undefined;
    const days = duration.days || months !== undefined ? duration.days : undefined;
    const hours = duration.hours || days !== undefined ? duration.hours : undefined;
    return (
        <span>
            {duration.years ? (
                <>
                    <MdText>{duration.years}</MdText>
                    <CaptionText>Y</CaptionText>
                </>
            ) : null}
            {months !== undefined ? (
                <>
                    {duration.years ? ' ' : ''}
                    <MdText>{months}</MdText>
                    <CaptionText>M</CaptionText>
                </>
            ) : null}
            {days !== undefined ? (
                <>
                    {duration.years || duration.months ? ' ' : ''}
                    <MdText>{days}</MdText>
                    <CaptionText>D</CaptionText>
                </>
            ) : null}
            {hours !== undefined ? (
                <>
                    {duration.years || duration.months || duration.days ? ' ' : ''}
                    <MdText>{hours}</MdText>
                    <CaptionText>H</CaptionText>
                </>
            ) : null}
            {duration.years || duration.months || duration.days || duration.minutes ? ' ' : ''}
            <MdText>
                {
                    // Downstream this component uses useNow() which will refresh our duration every 60 seconds.
                    // However, at least in the case of unit timers, we're liable to have minor discrepancies between
                    // how elapsed a timer is and the time that useNow() updates the "now value". Since we show these
                    // durations to users with minutes as the highest fidelity value, and to account for the afformentioned
                    // discrepancy, we will round up to 1 minute from 55 seconds to give the user a more accurate (given the presented level of fidelity)
                    // elapsed duration.
                    duration.seconds && duration.seconds > 55
                        ? duration.minutes ?? 0 + 1
                        : duration.minutes
                }
            </MdText>
            <CaptionText>M</CaptionText>
        </span>
    );
}
